import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { TRANSLATIONS } from '../utils/constants';
import type { SupportedLanguageCode } from '../utils/constants';

type AnyRecord = Record<string, unknown>;

const isPlainObject = (v: unknown): v is AnyRecord =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

// Recursive Proxy: returns the selected-language value when present and non-empty,
// otherwise transparently falls back to the English value at the same path.
const withFallback = <T extends object>(selected: T, fallback: T): T => {
  return new Proxy(selected, {
    get(target, prop, receiver) {
      const sel = Reflect.get(target, prop, receiver);
      const fb = Reflect.get(fallback as AnyRecord, prop);

      if (typeof sel === 'string') {
        return sel.length > 0 ? sel : (fb ?? sel);
      }
      if (isPlainObject(sel) && isPlainObject(fb)) {
        return withFallback(sel, fb);
      }
      return sel ?? fb;
    },
  }) as T;
};

export function useStrings() {
  const lang = useSelector((state: RootState) => state.app.selectedLanguage ?? 'en');
  const english = TRANSLATIONS['en'];
  const selected = TRANSLATIONS[lang as SupportedLanguageCode];

  return useMemo(() => {
    if (!selected) {
      console.error(`[i18n] Language "${lang}" not supported. Falling back to English.`);
      return english;
    }
    if (selected === english) return english;
    return withFallback(selected, english);
  }, [selected, english, lang]);
}
